/**
 * The wire contract.
 *
 * Every request body, response shape and stream event `/api/assist/*` speaks,
 * in one file, so the Worker and the SPA are built against the same document.
 * `worker/routes/*.ts` returns these types; `web/src/api.ts` type-imports them
 * through the `@wire` alias. Types, plus the few constants both sides must
 * agree on. Same arrangement as website-lms, for the same reason: the contract
 * is written first and several builders work from it at once.
 *
 * Rules every shape follows:
 *   · camelCase on the wire; the database is snake_case and the mapping
 *     happens once, in lib/store.ts.
 *   · unix **seconds** for every timestamp.
 *   · `null` means absent, never `-1` or `''`.
 *   · decrypted: nothing here carries ciphertext. Encryption is storage's
 *     business and ends at lib/store.ts.
 *
 * Errors are `{ error: { code, message, ...details }, requestId }` with the
 * codes listed in `ErrorCode`.
 */

// ── capabilities ────────────────────────────────────────────────────────────

/** What this deployment can do. GET /api/assist/config, and on `Me`. */
export interface Capabilities {
  /** The Workers AI binding exists, so the built-in models work. */
  ai: boolean;
  /** ENCRYPTION_KEY is set. False means every write refuses. */
  encryption: boolean;
  /** The R2 bucket is bound, so files and generated images work. */
  files: boolean;
  /** Mauvely-billed calls go through AI Gateway. */
  gateway: boolean;
  /** An organisation's AI switch is enforceable here. */
  appAccess: boolean;
  /** `/image` works (Workers AI). */
  image: boolean;
  /** Images can be changed as well as made: an edit, a resize, a cut-out. */
  imageEdit: boolean;
  /** A chat has a Linux computer in the cloud to run things on. */
  sandbox: boolean;
  /** Dictation on the web works (Workers AI Whisper). */
  transcribe: boolean;
  /** Live dictation and voice mode work: `/listen` and `/speak`. */
  voice: boolean;
  /** Which third-party providers Mauvely holds its own key for. */
  mauvelyKeys: { openai: boolean; anthropic: boolean; google: boolean };
  /** Models that use tools can search the web: a search provider is set up. */
  webSearch: boolean;
  /** Models that use tools can search Mauvely's documentation. */
  docsSearch: boolean;
}

// ── errors ──────────────────────────────────────────────────────────────────

export type ErrorCode =
  | 'unauthorized' //                401 — not signed in
  | 'second_factor_required' //      403 — signed in, TOTP not cleared
  | 'ai_disabled' //                 403 — details.reason: AiOffReason
  | 'forbidden'
  | 'not_found'
  | 'invalid_field' //               400 — details.field
  | 'busy' //                        409 — a reply is running in the chat: details.messageId, null until its message exists
  | 'bad_json'
  | 'bad_content_type'
  | 'model_unavailable' //           400 — the model id is unknown or its provider is not connected
  | 'file_too_large' //              413 — details.maxBytes
  | 'limit_reached' //               429 — details.window: 'fiveHour' | 'weekly' | 'month' | 'organisation', details.resetsAt
  | 'rate_limited' //                429
  | 'provider_error' //              502 — details.provider, details.status
  | 'no_images' //                   stream only — the reply typed an image's record instead of making it
  | 'encryption_unavailable' //      503
  | 'not_configured' //              503
  | 'internal';

export interface ApiErrorBody {
  error: { code: ErrorCode; message: string; [detail: string]: unknown };
  requestId: string;
}

// ── identity ────────────────────────────────────────────────────────────────

/** Why AI is off for this account. `personal`: the person switched it off on
 *  mauvely.com. `organisation`: their organisation did. */
/** `assist`: the person's organisation has not turned Assist on. */
export type AiOffReason = 'personal' | 'organisation' | 'assist';

export type PermLevel = 'allow' | 'ask' | 'block';

/** One tool of a remote server, with the level the person gave it. */
export interface ConnectorTool {
  name: string;
  title?: string;
  description?: string;
  /** The server says it only reads. */
  readOnly: boolean;
  /** Its arguments, a JSON Schema object, as the server gave them. */
  inputSchema?: Record<string, unknown>;
  /** `ask` until the person says otherwise: a call waits for them to allow it. */
  level: PermLevel;
}

/**
 * A remote MCP server the person added, reached by the Worker. `needs_auth`:
 * it wants a sign-in (`POST /connectors/:id/authorize`). `error`: it could
 * not be reached when last asked.
 */
export interface Connector {
  id: string;
  name: string;
  url: string;
  status: 'connected' | 'needs_auth' | 'error';
  tools: ConnectorTool[];
  /** When its tools were last read. */
  toolsAt: number | null;
  createdAt: number;
}

export interface ConnectorList {
  connectors: Connector[];
}

/** POST /connectors. `name` defaults to the server's host. */
export interface ConnectorCreate {
  url: string;
  name?: string;
}

/** PATCH /connectors/:id. `levels` sets tools' levels by name. */
export interface ConnectorPatch {
  name?: string;
  levels?: Record<string, PermLevel>;
}

/** POST /connectors/:id/authorize: where to send the person to sign in. */
export interface ConnectorAuthorize {
  authorizeUrl: string;
}

export interface Settings {
  /** A `ModelInfo.id`. */
  defaultModel: string;
  /** The effort a new chat starts on, where its model offers one. */
  defaultEffort: Effort;
  /** Show the third-party warning before a chat uses a third-party model. */
  warnThirdParty: boolean;
  /** The voice voice mode speaks in. */
  voice: VoiceName;
  /** A Mauvely developer's own switch: Assist is told it is talking with one,
   *  and may show its instructions. Refused for anyone else. */
  developerMode: boolean;
  /** IANA, as the person's browser reports it: what a new automation runs
   *  in. Null until a page has said. */
  timeZone: string | null;
  /** The model that makes images in chats and for `/image`. */
  imageModel: ImageModelKey;
}

export type SettingsPatch = Partial<Settings>;

/** The model a new chat uses until the person picks another. */
export const DEFAULT_MODEL = 'workers-ai:@cf/openai/gpt-oss-120b';

/**
 * How hard a reasoning model thinks before it answers. Each model offers a
 * subset (`ModelInfo.efforts`); a chat's effort that its model does not offer
 * is taken as the nearest one it does.
 */
export type Effort = 'low' | 'medium' | 'high';

export const EFFORTS: readonly Effort[] = ['low', 'medium', 'high'];

export const DEFAULT_EFFORT: Effort = 'medium';

export interface UsageWindow {
  /** 0–100, rounded. May exceed 100 only transiently; the SPA clamps. */
  pct: number;
  /** When the oldest counted event leaves the window. Null when nothing has
   *  been used in it. */
  resetsAt: number | null;
  /** Units used and allowed. `allowance` is null for an unlimited tier. */
  used: number;
  allowance: number | null;
}

/** A member's month when their organisation pays for Assist. */
export interface UsageMonth {
  /** What they have used this calendar month (UTC), in pence of the bill. */
  spentPence: number;
  /** Their monthly limit in pence; null for none. */
  limitPence: number | null;
  /** 0–100 against the limit; null without one. */
  pct: number | null;
  /** The start of next month. */
  resetsAt: number;
  /** The organisation's own monthly ceiling is reached. */
  organisationFull: boolean;
}

export interface UsageSummary {
  fiveHour: UsageWindow;
  weekly: UsageWindow;
  /** Present when an organisation pays: its monthly limit replaces the two
   *  windows, which then have no allowance. */
  month?: UsageMonth;
}

export interface Me {
  user: {
    id: string;
    name: string;
    /** The first word of `name`, for the greeting. Falls back to the email's
     *  local part. */
    firstName: string;
    email: string;
    /** Two letters for the avatar tile. */
    initials: string;
    /** The profile photo, from the account site; null for none. */
    avatarUrl: string | null;
  };
  /** The plan tier (`free` | `pro` | `max` | `enterprise`). */
  plan: string;
  ai: { allowed: boolean; reason: AiOffReason | null };
  /** The onboarding interview has been finished or skipped. */
  onboarded: boolean;
  settings: Settings;
  usage: UsageSummary;
  capabilities: Capabilities;
  /** Where to send someone to manage their account, e.g. to switch AI back on. */
  accountUrl: string;
}

// ── models and providers ────────────────────────────────────────────────────

export type ProviderKey =
  | 'workers-ai'
  | 'google'
  | 'openai'
  | 'anthropic'
  | 'openrouter'
  | 'custom'
  | 'ollama'
  | 'lmstudio';

/** The model menu's three groups: hosted by Mauvely on Cloudflare, running on
 *  this device (desktop only), and a third party's servers. */
export type ModelGroup = 'private' | 'device' | 'third';

export interface ModelInfo {
  /** `<provider>:<model>`, e.g. `workers-ai:@cf/openai/gpt-oss-120b`,
   *  `google:gemini-2.5-flash`. The only form a model is named in on the wire. */
  id: string;
  provider: ProviderKey;
  /** Full name for the menu, e.g. "gpt-oss 120B". */
  name: string;
  /** What the composer shows, e.g. "gpt-oss". */
  short: string;
  group: ModelGroup;
  /** Right-hand text in the menu row, e.g. "Google", "Ollama". */
  meta: string | null;
  /** Tokens. Drives the context ring. */
  contextWindow: number;
  /** Accepts images. */
  vision: boolean;
  /** Takes tools: web search, Research and the rest. Set in the catalogue. */
  tools?: boolean;
  /** Can work a window from screenshots: offered the desktop's
   *  `local__computer_screenshot` and `local__computer_click`. Set in the
   *  catalogue. */
  computerUse?: boolean;
  /** Who pays: Mauvely (counted against limits) or the person's own key. */
  billing: 'mauvely' | 'byok';
  /** The efforts this model offers, lowest first. Empty: it offers no choice,
   *  and the composer shows no effort control. */
  efforts: Effort[];
  /** Shown before "More models" in the menu. */
  featured?: boolean;
  /** Offered to Mauvely developers alone, for now. */
  preview?: boolean;
}

export interface ModelCatalogue {
  models: ModelInfo[];
  defaultModel: string;
}

/**
 * One row of Settings → Models.
 *
 *   builtin    Workers AI, always there when the binding is
 *   mauvely    a third party on Mauvely's key; the person may add their own
 *   connected  the person's own key or endpoint
 *   none       nothing configured
 *   desktop    LM Studio on the web: only the desktop app reaches it
 */
export type ProviderState = 'builtin' | 'mauvely' | 'connected' | 'none' | 'desktop';

export interface ProviderStatus {
  provider: ProviderKey;
  /** "Workers AI", "Google", "OpenRouter", "Other API"… */
  name: string;
  state: ProviderState;
  /** Last four characters of a stored key, for "Your key · …abcd". */
  keyHint: string | null;
  /** "Other API" and Ollama only. */
  baseUrl: string | null;
  /** How many models the provider offered when the key was checked. */
  modelCount: number | null;
}

/** PUT /providers/:provider. The key is checked (by listing models) before it
 *  is stored; a key that fails comes back as `provider_error`. */
export interface ProviderPut {
  /** Optional for `custom` and `ollama`. */
  apiKey?: string;
  /** Required for `custom` and `ollama` (an Ollama server's own address,
   *  public and https); ignored for the others. */
  baseUrl?: string;
}

// ── projects ────────────────────────────────────────────────────────────────

/** The project dot and icon tile. Token names, not colours. */
export type ProjectColour = 'violet' | 'mauve' | 'teal' | 'slate';

export interface ProjectSummary {
  id: string;
  name: string;
  /** A Phosphor glyph name. */
  icon: string;
  colour: ProjectColour;
  updatedAt: number;
}

export interface ProjectMemoryItem {
  id: string;
  text: string;
  createdAt: number;
}

export interface Project extends ProjectSummary {
  instructions: string;
  memory: ProjectMemoryItem[];
  files: FileInfo[];
  chats: ChatSummary[];
  /** Design mode for its chats, unless a chat says otherwise. */
  design: boolean;
  /** The design system its chats use, unless a chat names its own. */
  designSystemId: string | null;
}

export interface ProjectCreate {
  name?: string;
  icon?: string;
  colour?: ProjectColour;
}

export interface ProjectPatch {
  name?: string;
  instructions?: string;
  icon?: string;
  colour?: ProjectColour;
  design?: boolean;
  /** One of the account's design systems, or null for none. */
  designSystemId?: string | null;
}

// ── files ───────────────────────────────────────────────────────────────────

export interface FileInfo {
  id: string;
  name: string;
  mime: string;
  size: number;
  projectId: string | null;
  chatId: string | null;
  createdAt: number;
}

/** POST /files is multipart/form-data: `file`, and optionally `projectId` or
 *  `chatId`. Neither → a loose upload the next turn attaches. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

// ── chats and messages ──────────────────────────────────────────────────────

export type ChatMode = 'auto' | 'manual';

export interface ChatSummary {
  id: string;
  title: string;
  projectId: string | null;
  updatedAt: number;
  /** An approval is waiting in it. */
  needsYou: boolean;
  /** Never listed, reads and writes no memory, and has no project. Deleted
   *  once left, or after a day untouched, unless it is saved. */
  temporary: boolean;
  /** A reply is running in it: `GET /chats/:id/live` streams it. `messageId`
   *  is null until the reply's message exists. Null when none is. */
  live?: { messageId: string | null } | null;
  /** A run of this automation; null for an ordinary chat. */
  automationId?: string | null;
  /** An agent a reply in `parentChatId` started (start_agents): shown in
   *  that chat's Agents panel, watch-only, never in the lists. Null for any
   *  other chat. */
  agent?: { parentChatId: string; state: AgentState } | null;
  /** A PC's Dispatch thread: the `Device` it runs on. Never in the lists or
   *  a project; the hub tells of it as `dispatch`, not `chat`. Null for any
   *  other chat. */
  deviceId?: string | null;
}

/** An agent at work, or how its latest turn ended. */
export type AgentState = 'working' | 'done' | 'failed' | 'stopped';

export interface ContextUse {
  /** Tokens the last turn sent. */
  tokens: number;
  /** The chat model's window. */
  window: number;
  /** 0–100, rounded. */
  pct: number;
}

export interface Chat extends ChatSummary {
  model: string;
  /** Sent with every turn; ignored by a model that offers no choice. */
  effort: Effort;
  mode: ChatMode;
  messages: Message[];
  context: ContextUse;
  /** The agents its replies started, oldest first. */
  agents?: ChatSummary[];
  /** Design mode is on: the chat's own setting, or its project's. */
  design: boolean;
  /** The design system the chat names itself; null follows its project's. */
  designSystemId: string | null;
  /** The design system in use: the chat's own, or its project's
   *  (`inherited`). Null for none. */
  designSystem: { id: string; name: string; inherited: boolean } | null;
}

export interface ChatCreate {
  projectId?: string | null;
  model?: string;
  /** Defaults to `Settings.defaultEffort`. */
  effort?: Effort;
  mode?: ChatMode;
  /** A temporary chat. Its `projectId` is dropped. */
  temporary?: boolean;
}

export interface ChatPatch {
  title?: string;
  /** Refused while the chat is temporary, unless this PATCH also saves it. */
  projectId?: string | null;
  model?: string;
  effort?: Effort;
  mode?: ChatMode;
  /** Saves a temporary chat. A chat is never made temporary: `true` is a 400. */
  temporary?: false;
  design?: boolean;
  /** One of the account's design systems, or null to follow the project's. */
  designSystemId?: string | null;
}

export type MessageRole = 'user' | 'assistant';

/** `partial`: generation was stopped or failed part-way; the text is what
 *  arrived. */
export type MessageStatus = 'complete' | 'partial' | 'error';

export interface Attachment {
  fileId: string;
  name: string;
  mime: string;
  size: number;
}

export interface Step {
  /** Phosphor glyph. */
  icon: string;
  label: string;
  /** The app it ran in, e.g. "Compose". */
  app: string;
  /** A screenshot the step took, as a file id. */
  screenshotFileId?: string;
}

export interface QuizQuestion {
  q: string;
  /** The right answer. */
  a: string;
  /** The wrong ones. */
  wrong: string[];
}

/** What a message-body artifact (`kind: 'artifact'`) holds: the shape before
 *  artifacts were things of their own (`Artifact`). Still read, never made. */
export type ArtifactDoc =
  | { type: 'quiz'; questions: QuizQuestion[] }
  | { type: 'markdown'; text: string };

export interface Source {
  title: string;
  url: string;
}

/** Something the model looked up while answering. */
export interface Lookup {
  /** `web`: the open web. `docs`: Mauvely's documentation. */
  kind: 'web' | 'docs';
  query: string;
  /** What came back, in the order the model was given it. Empty while it is
   *  running, and when nothing was found. */
  sources: Source[];
  /** The lookup could not be made; the model was told and carried on. */
  failed?: boolean;
}

/**
 * A tool the desktop app runs on the person's machine, offered with a turn.
 * The name is `local__…` for the app's own tools and `mcp__<server>__<tool>`
 * for a local MCP server's.
 */
export interface ClientTool {
  name: string;
  description: string;
  /** A JSON Schema object. */
  parameters: Record<string, unknown>;
}

/**
 * What a Mauvely app has open, sent with a turn from its panel (`/panel`):
 * the app's key and what its `mcp__<app>__get_context` answered. Kept on the
 * person's message as `body.host`, and read for that message's reply only.
 */
export interface HostContext {
  /** The app's key, e.g. `compose`: `HOST_APP`. */
  app: string;
  /** At most `MAX_HOST_CHARS`. Empty when the app did not answer in time, or
   *  serves no get_context. */
  text: string;
}

export const HOST_APP = /^[a-z]{2,16}$/;
export const MAX_HOST_CHARS = 8_192;

/** Where a tool ran: the person's machine (the desktop app), or a connector
 *  the Worker reaches. `assist`: in Assist itself, such as loading a skill or
 *  starting agents. `page`: in Assist's own page on the person's device, as
 *  `run_code` does. */
export type ToolPlace = 'desktop' | 'connector' | 'sandbox' | 'assist' | 'page';

/** What `run_code` runs, in the page (web/src/runner.ts). */
export const CODE_LANGUAGES = ['python', 'javascript'] as const;
export type CodeLanguage = (typeof CODE_LANGUAGES)[number];
/** Where the page fetches Pyodide's files for the runner: the one address
 *  outside Assist its CSP lets it fetch from (worker/lib/headers.ts). */
export const PYODIDE_URL = 'https://cdn.jsdelivr.net/pyodide/v314.0.7/full/';

/** The desktop app's computer-use tools (docs/desktop-bridge.md, "Computer
 *  use"): `local__computer_<tool>`. */
export const COMPUTER_TOOL_PREFIX = 'local__computer_';
/** The ones that work from screenshots, offered only to a model the catalogue
 *  marks `computerUse`. */
export const COMPUTER_PIXEL_TOOLS: readonly string[] = [
  'local__computer_screenshot',
  'local__computer_click',
  'local__computer_drag',
  'local__computer_move'
];

/** A tool a reply called that is not a lookup or an image, as the row under
 *  the reply shows it. */
export interface ToolRun {
  /** The call's id; for a task of the old `delegate`, the call's id and
   *  `.<n>`, the task's number from 1. */
  id: string;
  tool: string;
  /** What it does, in a few words. */
  label: string;
  where: ToolPlace;
  state: 'running' | 'waiting' | 'approval' | 'done' | 'denied' | 'failed';
  /** What it was given, as its row opens to show it: a command or code as
   *  written, anything else as its arguments. Cut to fit. */
  input?: string;
  /** What came back, as the model read it. Cut to fit; absent until done. */
  output?: string;
  /** The images it gave back, drawn on its row: a computer call's
   *  screenshot. Other calls' images go under the reply, in `files`. */
  files?: Attachment[];
  /** The video `generate_video` made, a Gallery item, played on its row. */
  video?: { id: string; width: number; height: number };
  /** The artifact `create_artifact` or `update_artifact` made, and the
   *  version it made, drawn on its row as a tile that opens it. */
  artifact?: { id: string; version: number; title: string; kind: ArtifactKind };
}

/**
 * A call the reply is waiting on. `result`: the desktop app runs it, or the
 * page does where it is `page`, and sends back what came out. `approval`: the
 * person allows or denies it, and the Worker runs it.
 */
export interface PendingCall {
  id: string;
  name: string;
  /** JSON text, as the model wrote it. */
  arguments: string;
  label: string;
  where: ToolPlace;
  needs: 'result' | 'approval';
}

/** Calls of Assist's own (`where: 'assist'`) that the Worker answers itself
 *  once what they wait on is over, a Research job, agents at work or a
 *  video being made: no person and no machine is needed, so a reply waiting
 *  only on these does not wait for you. */
export const SERVER_WAIT_TOOLS: readonly string[] = ['research', 'wait_agents', 'generate_video'];

/** An image a reply made or changed. */
export interface GeneratedImage {
  fileId: string;
  /** What it was made from; for an exact change, what was done. */
  prompt: string;
  width: number;
  height: number;
}

/** One question in an `Ask`. */
export interface AskQuestion {
  question: string;
  /** Two to four, each short. */
  options: string[];
  /** More than one option may be picked. */
  multiSelect: boolean;
}

/** The questions the model asked instead of guessing, together. Answered in
 *  the question dock, one answer each, or by sending anything else as the
 *  next message. */
export interface Ask {
  /** One to four. */
  questions: AskQuestion[];
  /** What the person sent in reply; null until they do. */
  answer: string | null;
}

/**
 * One call to the model within a reply, and the calls it made: how far the
 * reply's `reasoning` and `text` (in characters) and its `lookups`, `calls`
 * and `images` (in items) had reached when it ended. A round starts where the
 * one before it ended.
 */
export interface ReplyRound {
  reasoning: number;
  text: number;
  lookups: number;
  calls: number;
  images: number;
  /** Seconds from this round's first thought to its first word, or to its
   *  end when it wrote none. Absent when it showed no thinking. */
  thoughtSeconds?: number;
}

/** One answer from the question dock, with the question it answers. */
export interface AnswerItem {
  question: string;
  answer: string;
}

/**
 * What a message holds. `text` is the ordinary case; the rest are produced by
 * the tool loop (desktop, a later session) and by `/image`, and render as the
 * cards in the prototype.
 */
export type MessageBody =
  | {
      kind: 'text';
      text: string;
      attachments?: Attachment[];
      /** What the model thought before answering, when it showed it. Never
       *  sent back to a model as history. */
      reasoning?: string;
      /** Seconds from the first thought to the first word of the answer. */
      thoughtSeconds?: number;
      /** What the model looked up before answering, in order. */
      lookups?: Lookup[];
      /** The model asked a question; the reply ends with it. */
      ask?: Ask;
      /** What memory upkeep changed after this reply, for "Memory updated". */
      memory?: MemoryChange[];
      /** A person's message made in the question dock: drawn as its answers
       *  rather than as `text`, which lists them for the model. */
      answered?: AnswerItem[];
      /** Images the reply made or changed, in order, drawn after its text. */
      images?: GeneratedImage[];
      /** Tools it called on the person's machine, a connector or the chat's
       *  computer, in order. */
      calls?: ToolRun[];
      /** Files it made on the chat's computer and gave the person, and the
       *  images its calls on the machine or in an app gave back. */
      files?: Attachment[];
      /** The reply stopped to wait on these; `continue` takes it on. */
      pending?: PendingCall[];
      /** The reply stopped after this many rounds of calls, to wait for the
       *  person to say whether it goes on: `continue` takes it on, or with
       *  `stop` ends it there. */
      stepLimit?: number;
      /** A person's message sent from an app's panel: what the app had open. */
      host?: HostContext;
      /** A person's message: the part of an earlier reply it answers, as they
       *  selected it. */
      quote?: string;
      /** A person's message from an artifact's "Change this…": the artifact
       *  it asks to change. */
      artifact?: { id: string; title: string };
      /** Not the person's words: Assist's note that the chat began as a copy
       *  of a page someone shared (Remix), which `text` tells the model.
       *  Drawn as a notice, with the copy to open. */
      remix?: { title: string; artifactId: string };
      /** What the person sent while the reply was working, taken on with it
       *  (`ContinueRequest.steer`): each after the round it followed, counting
       *  from 1, as `rounds` counts them. */
      steers?: Array<{ round: number; text: string }>;
      /** Where each call to the model ended, in order, so each round's
       *  thinking and calls are drawn between the text around them. Absent
       *  on a reply of one round, and on one stored before rounds were kept. */
      rounds?: ReplyRound[];
      /** Not the person's words: Assist's own note that these agents ended,
       *  whose reports `text` carries for the model. Drawn as a notice. */
      agents?: Array<{ chatId: string; title: string; state: Exclude<AgentState, 'working'> }>;
      /** A reply that failed with nothing kept: what the person was told, as
       *  the `error` event's message said it. Never sent to a model. */
      failure?: string;
      /** A reply in a PC's Dispatch thread, waiting on that PC: why it has
       *  not gone on. Gone once the PC takes it on. */
      deviceWait?: DeviceWait;
    }
  | {
      kind: 'steps';
      running: boolean;
      steps: Step[];
      undoable?: boolean;
      undone?: boolean;
    }
  | { kind: 'approval'; title: string; app: string; state: 'pending' | 'allowed' | 'denied' }
  | { kind: 'question'; question: string; options: string[]; answer: string | null }
  | { kind: 'artifact'; title: string; subtitle: string; doc: ArtifactDoc }
  | {
      kind: 'image';
      fileId: string;
      prompt: string;
      /** The image's size, so its box is kept while it loads. Absent on an
       *  image made before sizes were kept. */
      width?: number;
      height?: number;
    };

export type MessageKind = MessageBody['kind'];

export interface Message {
  id: string;
  role: MessageRole;
  /** The model that wrote it; null on a user message. */
  model: string | null;
  status: MessageStatus;
  createdAt: number;
  body: MessageBody;
  /** Where this message sits among the versions of it — an edit is a new
   *  version beside the one edited. Absent when there is only the one. */
  branch?: MessageBranch;
  /** A reply's figures: kept for a Mauvely developer's replies alone. */
  stats?: ReplyStats;
}

/** What a reply took. */
export interface ReplyStats {
  /** A `ModelInfo.id`. */
  model: string;
  /** Who paid for the model: Mauvely, or the person's own key. */
  billing: 'mauvely' | 'byok';
  tokensIn: number;
  tokensOut: number;
  /** What it cost Mauvely, in units (1/100,000 USD), the images it made
   *  included. Zero on the person's own key. */
  units: number;
  /** Time spent working, with any wait for the person or their machine left
   *  out. */
  ms: number;
  /** Calls to the model. */
  rounds: number;
  /** The Worker's request id for each stretch of work, oldest first. */
  requestIds: string[];
}

/** GET /chats/:id/messages/:messageId/debug — a developer's Details. */
export interface ReplyDebug {
  stats: ReplyStats;
  /** Null when none was kept. */
  detail: ReplyDetail | null;
}

/** What a reply was made from, each part cut to fit. */
export interface ReplyDetail {
  /** The system prompt its last stretch was sent. */
  system: string;
  /** Its rounds after the chat's context: what it said and called, and what
   *  each call gave back. */
  exchange: DebugTurn[];
}

export interface DebugTurn {
  role: 'user' | 'assistant' | 'tool';
  /** What was said, or a call's result. */
  text: string;
  /** An assistant turn's calls. */
  calls?: Array<{ name: string; arguments: string }>;
  /** The tool a result came from. */
  name?: string;
}

/** GET /api/assist/usage/history — a developer's usage, by day and by kind. */
export interface UsageHistory {
  days: number;
  /** Units on Mauvely's account by UTC day and purpose. Days with none are
   *  left out. */
  byDay: Array<{ day: string; purpose: string; units: number }>;
  byModel: Array<{ model: string; source: 'mauvely' | 'byok'; calls: number; tokensIn: number; tokensOut: number; units: number }>;
  byPurpose: Array<{ purpose: string; calls: number; units: number }>;
}

/** A message's place among its versions, oldest first. */
export interface MessageBranch {
  /** Counting from 1. */
  at: number;
  of: number;
  /** The version before and after this one, for POST /chats/:id/leaf. */
  prev?: string;
  next?: string;
}

/** POST /chats/:id/turn and POST /onboarding/turn. */
export interface TurnRequest {
  text: string;
  /** File ids from POST /files. */
  attachments?: string[];
  /** Overrides the chat's model for this turn and becomes the chat's model. */
  model?: string;
  /** The id of the assistant message whose `ask` this text answers. Its
   *  `ask.answer` becomes `text`. An ask already answered is left as it is. */
  answers?: string;
  /** With `answers`: the dock's answers, one per question in order. The
   *  message is then stored as `answered`, and `text` is made from them. */
  picks?: string[];
  /** The id of a message of the person's this one replaces: it becomes a new
   *  version beside it, keeping its attachments, and the reply goes on from
   *  there. The old version and what followed it stay. */
  edits?: string;
  /** The desktop app's tools for this turn. */
  clientTools?: ClientTool[];
  /** The page runs code itself: `run_code` is offered. */
  runsCode?: boolean;
  /** Voice mode: the reply is said aloud, so it is written to be heard, at
   *  low effort, and never waits on a question or an approval. */
  voice?: boolean;
  /** Sent from an app's panel: what the app has open. */
  host?: HostContext;
  /** The part of an earlier reply this message answers, as the person
   *  selected it: at most `MAX_QUOTE_CHARS`. An edit keeps the quote of the
   *  message it replaces. */
  quote?: string;
  /** An artifact of this chat the message asks to change ("Change this…"):
   *  its id. An edit keeps the artifact of the message it replaces. */
  artifact?: string;
  /** Research is on: the reply may research the question in depth, once.
   *  Paid plans only, and a model that takes tools; ignored otherwise. */
  research?: boolean;
  /** A reply running in the chat is stopped and this turn takes its place,
   *  rather than 409 `busy`. */
  replaceRunning?: boolean;
  /** A reply running in the chat keeps going, and this message is held until
   *  it ends, then sent (202 `{queued: true}`), rather than 409 `busy`. One
   *  message is held per chat; a second joins it. What a notification's Reply
   *  sends. */
  queue?: boolean;
}

/** The longest quote a message can carry. */
export const MAX_QUOTE_CHARS = 2_000;

/**
 * POST /chats/:id/messages/:messageId/continue — take on a reply that is
 * waiting (`body.pending` or `body.stepLimit`). One result for each `result`
 * call and one decision for each `approval` call; a call left out is skipped,
 * and the model is told so. Answers with the turn stream, into the same
 * message.
 */
export interface ContinueRequest {
  /** `files`: the images the call gave back, as file ids from POST /files, at
   *  most `MAX_RESULT_IMAGES`. They show under the reply as its `files`, and
   *  a model that sees images is shown them. */
  results?: Array<{ callId: string; content: string; isError?: boolean; files?: string[] }>;
  approvals?: Array<{ callId: string; decision: 'allowed' | 'allowed_chat' | 'denied' }>;
  /** End the reply where it stands, making none of the calls it waits on:
   *  stored as stopped, and the stream carries only `done`. */
  stop?: boolean;
  clientTools?: ClientTool[];
  runsCode?: boolean;
  /** What the person sent while the reply worked: the model reads it after
   *  the results, before its next step. At most `MAX_STEER_CHARS`. */
  steer?: string;
  research?: boolean;
}

export const MAX_RESULT_IMAGES = 4;

/** GET /chats/:id/messages/:messageId/research — where the Research job a
 *  reply waits on stands. Once it is `done` or `failed`, `continue` takes
 *  the reply on. */
export interface ResearchStatus {
  state: 'running' | 'done' | 'failed';
}
/** The longest message the person can send into a reply as it works. */
export const MAX_STEER_CHARS = 4_000;

/** POST /chats/:id/leaf — show this version of a message, and the newest
 *  replies under it. Answers with the `Chat`. */
export interface LeafRequest {
  messageId: string;
}

/** POST /chats/:id/retry — regenerate this assistant message (the last one). */
export interface RetryRequest {
  messageId: string;
  model?: string;
  /** The desktop app's tools for this turn. */
  clientTools?: ClientTool[];
  runsCode?: boolean;
  research?: boolean;
  /** As `TurnRequest.replaceRunning`. */
  replaceRunning?: boolean;
}

/** POST /chats/:id/branch — a new chat holding the messages up to and
 *  including this one. Answers with the new `Chat`. */
export interface BranchRequest {
  messageId: string;
}

/** POST /chats/:id/messages/:messageId/answer — pick an option on a question
 *  card or allow/deny an approval card. */
export interface CardAnswer {
  answer?: string;
  approval?: 'allowed' | 'allowed_chat' | 'denied';
}

// ── the turn stream ─────────────────────────────────────────────────────────

/** One line of human-readable memory change, e.g. `Preferences · UK spelling`. */
export interface MemoryChange {
  scope: 'account' | 'project';
  op: 'added' | 'removed' | 'created' | 'deleted';
  /** Doc name (account) or project name (project). */
  target: string;
  text: string;
}

/**
 * Server-sent events. Each arrives as `event: <type>\ndata: <JSON of the whole
 * event>\n\n`, in roughly this order:
 *
 *   attach      GET /chats/:id/live only, first: the reply being joined. The
 *               events so far follow it, runs of `token` and `reasoning`
 *               joined and each `lookup`, `tool` and `image` as it stands now,
 *               then the rest as they happen
 *   user        the stored user message (absent on retry and the first
 *               onboarding turn)
 *   start       the assistant message's id, before any text
 *   round       the model is asked again, after the calls before it: what
 *               follows is a new round (`ReplyRound`). Also the first model
 *               call of a reply taken on after a pause
 *   thinking    the model has started reasoning (once)
 *   reasoning   a piece of that reasoning, as it arrives
 *   lookup      a lookup started (`running`) or finished; `index` counts from
 *               0 within the reply, and a finished one replaces its start
 *   image       an image started (no `fileId` yet), finished or failed;
 *               `index` counts from 0 within the reply, as a lookup's does
 *   tool        a call on the person's machine, a connector or Assist itself
 *               changed state; `index` counts from 0 within the reply
 *   tool_request the reply stopped to wait on `message.body.pending`, or at
 *               its `stepLimit`, in place of `done`; `continue` takes it on
 *   steer       a continue carried what the person sent while the reply
 *               worked; it follows the rounds so far (`MessageBody.steers`)
 *   token       a piece of the reply
 *   done        the stored assistant message, usage and context
 *   title       the chat got its title (first three turns only)
 *   suggestion  a short reply the person might send next (Tab to use); none
 *               after a reply that ends with an `ask`
 *   memory      what memory upkeep changed, after `done`
 *   usage       the account's usage once everything the turn spent is
 *               counted; the last event
 *   finished    onboarding only: the interview is over
 *   error       terminal; the stream ends after it
 *
 * Reasoning, lookups and tokens may interleave: a model that looks something
 * up thinks again before it answers.
 *
 * A client ignores an event type it does not know.
 *
 * The stream closes after `done` plus whatever follows it, or after `error`.
 * Between events, a `: ping` comment line arrives every 20 seconds while the
 * stream is open; it carries nothing.
 */
export type TurnEvent =
  | {
      type: 'attach';
      kind: 'turn' | 'retry' | 'continue';
      /** Null until the reply's message exists (`start`). */
      messageId: string | null;
      /** A continue's reply as it was taken on, which the events go on from. */
      base: MessageBody | null;
    }
  | { type: 'user'; message: Message }
  | { type: 'start'; messageId: string; model: string }
  | { type: 'round' }
  | { type: 'thinking' }
  | { type: 'reasoning'; text: string }
  | { type: 'lookup'; index: number; lookup: Lookup; running: boolean }
  | {
      type: 'image';
      index: number;
      prompt: string;
      width: number;
      height: number;
      /** Null while it is being made, and when it failed. */
      fileId: string | null;
      failed?: boolean;
    }
  | { type: 'token'; text: string }
  | { type: 'tool'; index: number; run: ToolRun }
  | { type: 'steer'; text: string }
  | { type: 'tool_request'; message: Message; usage: UsageSummary; context: ContextUse }
  | { type: 'done'; message: Message; usage: UsageSummary; context: ContextUse }
  | { type: 'title'; chatId: string; title: string }
  | { type: 'suggestion'; text: string }
  | { type: 'memory'; changes: MemoryChange[] }
  | { type: 'usage'; usage: UsageSummary }
  | { type: 'finished' }
  | { type: 'error'; code: ErrorCode; message: string; resetsAt?: number };

// ── artifacts ───────────────────────────────────────────────────────────────

/**
 * Something a reply made to be looked at and kept, beside the chat: made by
 * the model's `create_artifact`, changed by its `update_artifact`, each change
 * a new version with the earlier ones kept. Belongs to the chat whose reply
 * made it, and goes with it.
 *
 * `html`, `svg` and `react` are drawn in a frame of their own, with no
 * origin (`ARTIFACT_FRAME_PATH`); `markdown` and `quiz` are drawn by the
 * page. `react` is one React component in JSX or TSX, which the page
 * compiles to script before the frame is handed it.
 */
export const ARTIFACT_KINDS = ['html', 'svg', 'markdown', 'quiz', 'react'] as const;
export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];

/** The most one version holds, in characters. */
export const MAX_ARTIFACT_CHARS = 400_000;
export const MAX_ARTIFACT_TITLE_CHARS = 80;

/**
 * Who may open an artifact's link (`/a/<token>`), as its owner chose.
 * `private`: nobody but its owner, in the chat. `people`: anyone signed in
 * whose account's email is one the owner named. `organisation`: anyone
 * signed in who is in the owner's organisation, both read on each request.
 * `link`: anyone with the link, with no account.
 */
export const SHARE_LEVELS = ['private', 'people', 'organisation', 'link'] as const;
export type ShareLevel = (typeof SHARE_LEVELS)[number];

/** The most people one link names. */
export const MAX_SHARE_PEOPLE = 20;

export interface ArtifactShare {
  level: ShareLevel;
  /** The emails a `people` link opens for, as the owner gave them; empty
   *  otherwise. */
  people: string[];
  /** The link's path, `/a/<token>` on Assist's own address; null while
   *  private. */
  path: string | null;
  /** The version the link shows; null: always the latest. */
  version: number | null;
  /** Unix seconds; null for none. */
  expiresAt: number | null;
}

export interface ArtifactSummary {
  id: string;
  chatId: string;
  title: string;
  kind: ArtifactKind;
  /** The latest version, counting from 1. */
  version: number;
  share: ArtifactShare;
  createdAt: number;
  updatedAt: number;
}

export interface ArtifactVersion {
  version: number;
  content: string;
  /** The tokens of the design system it was made with, CSS custom
   *  properties on `:root`, set in its frame before its own styles. Null
   *  for none. */
  tokens: string | null;
  /** The reply that made this version read something from outside — the web,
   *  a connector, the person's machine, an attachment, a project file or an
   *  app's panel. A page or an SVG that did is run only on a click, never by
   *  itself, so what it read cannot be sent out without the person's say. */
  unsafe: boolean;
  createdAt: number;
}

/** GET /artifacts/:id — the artifact with its latest version whole. */
export interface Artifact extends ArtifactSummary {
  latest: ArtifactVersion;
  /** Every version, oldest first. */
  versions: Array<{ version: number; createdAt: number }>;
  /** What its owner may share it as now: `organisation` only while they
   *  are in one. */
  shareLevels: ShareLevel[];
}

/** GET /artifacts?chat=<id> — a chat's artifacts, oldest first. */
export interface ArtifactList {
  artifacts: ArtifactSummary[];
}

/** GET /artifacts with no `chat` — every artifact of the account's, the most
 *  recently changed first, `ARTIFACT_PAGE` at a time: `before` is the
 *  `next` of the page before, null after the last. */
export interface ArtifactLibrary {
  artifacts: Array<ArtifactSummary & { chatTitle: string }>;
  next: string | null;
}
export const ARTIFACT_PAGE = 30;

/** PUT /artifacts/:id/share — answers with the `ArtifactShare`. `private`
 *  ends the link; sharing again makes a new one. Moving between
 *  `organisation` and `link` keeps it. */
export interface ShareRequest {
  level: ShareLevel;
  /** The version the link shows; null: always the latest. Absent: the
   *  latest version as it is now. */
  version?: number | null;
  /** Unix seconds, in the future; null or absent for none. */
  expiresAt?: number | null;
  /** Emails, for `people`: at least one, at most `MAX_SHARE_PEOPLE`. */
  people?: string[];
}

/** GET /shared/:token — what a link shows, and nothing of who made it, their
 *  chat or their account. */
export interface SharedArtifact {
  title: string;
  kind: ArtifactKind;
  version: number;
  content: string;
  tokens: string | null;
  /** The version was made from something read from outside: it runs on a
   *  click, not by itself (`ArtifactVersion.unsafe`). */
  unsafe: boolean;
  /** When this version was made. */
  createdAt: number;
  /** A viewer signed in can copy it into their own Assist
   *  (`POST /shared/:token/remix`); false for a link to anyone opened
   *  signed out. */
  remix: boolean;
}

/** POST /shared/:token/remix — what the link shows, copied into the signed-in
 *  viewer's own account as the artifact of a new chat of theirs. 201. */
export interface RemixResult {
  chatId: string;
  artifactId: string;
}

/** The hosts an artifact may load scripts, styles, fonts and images from,
 *  and fetch from; Google Fonts besides for styles and fonts. The model is
 *  told the same list (prompts.ts, `create_artifact`). */
export const ARTIFACT_HOSTS = ['https://cdnjs.cloudflare.com', 'https://cdn.jsdelivr.net', 'https://unpkg.com'] as const;

/**
 * The artifact frame's policy, less what only a response header can carry
 * (`frame-ancestors` and `sandbox`, added in worker/lib/headers.ts). A page
 * exported as a file carries this as its own, so it reaches what it reached
 * in the frame and nothing more.
 */
export function artifactPolicy(): string[] {
  const hosts = ARTIFACT_HOSTS.join(' ');
  return [
    "default-src 'none'",
    `script-src 'unsafe-inline' 'unsafe-eval' blob: ${hosts}`,
    `style-src 'unsafe-inline' ${hosts} https://fonts.googleapis.com`,
    `font-src data: ${hosts} https://fonts.gstatic.com`,
    `img-src data: blob: ${hosts}`,
    'media-src data: blob:',
    `connect-src ${hosts}`,
    'worker-src blob:',
    "frame-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
    "object-src 'none'"
  ];
}

/** The frame an `html`, `svg` or `react` artifact is drawn in, framed with
 *  `sandbox="allow-scripts"` and served with a policy of its own
 *  (worker/lib/headers.ts). The page hands it what to draw by `postMessage`,
 *  once per frame: `{type: 'ready'}` from the frame, then
 *  `{type: 'render', html}` to it, the whole document with the tokens in
 *  (web/src/artifactFrame.ts). */
export const ARTIFACT_FRAME_PATH = '/artifact.html';

// ── design systems ──────────────────────────────────────────────────────────

/**
 * A look an account keeps: tokens as CSS custom properties, and guidance the
 * model reads. A project names one for its chats, and a chat may name its
 * own; while one is in use, what a reply makes or changes is made with its
 * guidance, and its tokens are set in the artifact's frame.
 */
export interface DesignSystem {
  id: string;
  name: string;
  /** `:root { --name: value; … }`, as stored. */
  tokens: string;
  /** Markdown. */
  guidance: string;
  createdAt: number;
  updatedAt: number;
}

export interface DesignSystemList {
  designSystems: DesignSystem[];
}

/** POST /design-systems. `tokens` is CSS custom properties, or a tokens JSON
 *  (nested names joined with `-`); either is kept as CSS. */
export interface DesignSystemCreate {
  name: string;
  tokens: string;
  guidance?: string;
}

/** PATCH /design-systems/:id. */
export type DesignSystemPatch = Partial<DesignSystemCreate>;

/** POST /design-systems/from-artifact — an artifact's look, read by a model
 *  into tokens and guidance, kept as a new design system. Answers with it. */
export interface DesignSystemFromArtifact {
  artifactId: string;
  /** Defaults to the artifact's title. */
  name?: string;
}

/** POST /shared/:token/report — anyone with a shared link can report it. No
 *  account. Only the link, the reason and the time are kept. */
export interface ReportRequest {
  /** Why, in a few words. Optional. */
  reason?: string;
}
export const MAX_REPORT_REASON_CHARS = 500;

export const MAX_DESIGN_SYSTEMS = 50;
export const MAX_DESIGN_SYSTEM_NAME_CHARS = 60;
export const MAX_DESIGN_TOKENS_CHARS = 32_000;
export const MAX_DESIGN_GUIDANCE_CHARS = 16_000;

// ── the account's hub ───────────────────────────────────────────────────────

/**
 * GET /api/assist/hub — one WebSocket per page, to the account's hub. JSON
 * frames, each with a `t`; either side ignores a frame it does not know.
 *
 * The page says which chat it has in front and when it was last used: that is
 * all the server knows of whether someone is at a PC, and a phone notification
 * goes only when nobody is. The hub sends what changed in the account's
 * chats, so every device's list stays current. `ping` every 30 s is answered
 * `pong`.
 *
 * A refusal closes the socket with a `HUB_CLOSE` code, and the page stops.
 *
 * The desktop app opens it as `/api/assist/hub?device=<Device.id>` once it
 * has published itself (`PUT /devices/:id`): the PC is online while one such
 * socket is, and only its sockets hear `wake` and `cancel`. Refused (4403)
 * from any page that is not the desktop app's, or with an id that is not
 * `DEVICE_ID`.
 */
export type HubClientFrame =
  | { t: 'state'; chatId: string | null; visible: boolean; focused: boolean }
  | { t: 'active' }
  | { t: 'ping' };

export type HubServerFrame =
  /** A chat changed: a reply began or ended, it was titled, made or renamed. */
  | { t: 'chat'; chat: ChatSummary }
  /** A PC's Dispatch thread changed, as `chat` says of a listed chat. */
  | { t: 'dispatch'; chat: ChatSummary }
  /** A PC published itself: new, renamed, or its tools changed. */
  | { t: 'device'; device: Device }
  /** A PC's desktop app opened its socket, or the last one closed. */
  | { t: 'device_online'; deviceId: string; online: boolean }
  /** A PC was forgotten, and its thread with it. */
  | { t: 'device_gone'; deviceId: string }
  /** To that PC's sockets alone: a reply in its thread waits on its tools.
   *  Take it on (docs/dispatch.md). */
  | { t: 'wake'; chatId: string; messageId: string }
  /** To that PC's sockets alone: a reply in its thread was stopped. Stop
   *  its calls and let go of what it was granted. `messageId` is null when
   *  no reply was running or waiting. */
  | { t: 'cancel'; chatId: string; messageId: string | null }
  /** To that PC's sockets alone: a phone asks to be paired. Hand it to the
   *  bridge's `pair` and report its answer (docs/dispatch.md). */
  | ({ t: 'pair' } & Omit<PairRequest, 'v'>)
  /** To that PC's sockets alone: a paired phone answered one of its sheets.
   *  Hand it to the bridge's `approve`. */
  | ({ t: 'approval'; chatId: string; nonce: string } & ApprovalAnswer)
  /** A chat was deleted. */
  | { t: 'gone'; chatId: string }
  /** The projects changed: read them again. */
  | { t: 'lists' }
  /** A video started in the Gallery is done or failed; `item` once done. */
  | { t: 'video'; job: VideoJob; item: GalleryItem | null }
  | { t: 'pong' };

export const HUB_CLOSE = { signedOut: 4401, refused: 4403, unavailable: 4503 } as const;

// ── skills ──────────────────────────────────────────────────────────────────

/**
 * Instructions the person wrote once and runs by name. `/name` at the start
 * of a message runs one for that reply. One with `auto` on may also be
 * loaded by Assist when its description fits, on a model that uses tools.
 */
export interface Skill {
  id: string;
  /** What follows the slash: lower-case letters, digits and hyphens, starting
   *  with a letter, 32 at most. Unique within the account. */
  name: string;
  /** Up to 200 characters. */
  description: string;
  /** Up to 16 KB. */
  instructions: string;
  /** Off: neither `/name` nor Assist runs it. */
  enabled: boolean;
  /** Assist may load it by itself, by its description. */
  auto: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface SkillList {
  skills: Skill[];
}

/** POST /skills. `enabled` and `auto` default to true. */
export interface SkillCreate {
  name: string;
  description: string;
  instructions: string;
  enabled?: boolean;
  auto?: boolean;
}

/** PATCH /skills/:id. */
export type SkillPatch = Partial<SkillCreate>;

/**
 * The slash commands Assist comes with, run as a skill is (`/name` and what
 * to work on). What each tells the model is `BUILTIN_SKILL_TEXT` in
 * worker/lib/engine/prompts.ts. A skill of the person's own with the same
 * name runs instead, and a new skill cannot take one of these names.
 */
export const BUILTIN_SKILLS = [
  { name: 'tldr', group: 'writing', description: 'The gist in a few sentences', placeholder: 'Paste the text, or leave it empty for the last reply' },
  { name: 'rewrite', group: 'writing', description: 'Rewrite text more clearly', placeholder: 'Paste the text to rewrite' },
  { name: 'proofread', group: 'writing', description: 'Fix spelling and grammar', placeholder: 'Paste the text to check' },
  { name: 'translate', group: 'writing', description: 'Translate into another language', placeholder: 'The text, and the language to translate into' },
  { name: 'tone', group: 'writing', description: 'Change the tone', placeholder: 'The text, and the tone you want' },
  { name: 'expand', group: 'writing', description: 'Turn notes into full text', placeholder: 'Paste your notes' },
  { name: 'shorten', group: 'writing', description: 'Make text shorter', placeholder: 'Paste the text to shorten' },
  { name: 'explain', group: 'code', description: 'Explain code step by step', placeholder: 'Paste the code' },
  { name: 'review', group: 'code', description: 'Review code for bugs and risks', placeholder: 'Paste the code to review' },
  { name: 'debug', group: 'code', description: 'Find and fix a bug', placeholder: 'The code and the error' },
  { name: 'tests', group: 'code', description: 'Write tests for code', placeholder: 'Paste the code to test' },
  { name: 'regex', group: 'code', description: 'Write or explain a regex', placeholder: 'What it should match, or the regex' },
  { name: 'sql', group: 'code', description: 'Write or explain a SQL query', placeholder: 'What you need, or the query' },
  { name: 'refactor', group: 'code', description: 'Restructure code, same behaviour', placeholder: 'Paste the code' },
  { name: 'email', group: 'work', description: 'Draft an email', placeholder: 'Who it is to, and what it should say' },
  { name: 'meeting-notes', group: 'work', description: 'Turn notes into minutes and actions', placeholder: 'Paste the notes or transcript' },
  { name: 'plan', group: 'work', description: 'Break a goal into steps', placeholder: 'What you want to get done' },
  { name: 'brainstorm', group: 'work', description: 'Many ideas, fast', placeholder: 'What the ideas are for' },
  { name: 'pros-cons', group: 'work', description: 'Weigh up options', placeholder: 'The options, and what matters' },
  { name: 'eli5', group: 'work', description: 'Explain something simply', placeholder: 'What to explain' }
] as const satisfies ReadonlyArray<{ name: string; group: 'writing' | 'code' | 'work'; description: string; placeholder: string }>;

export type BuiltinSkillName = (typeof BUILTIN_SKILLS)[number]['name'];
export type BuiltinSkillGroup = (typeof BUILTIN_SKILLS)[number]['group'];

// ── automations ─────────────────────────────────────────────────────────────

export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export const WEEKDAYS: readonly Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** When an automation runs, in its time zone: every hour at `at`'s minutes,
 *  or at `at` every day, every weekday (Monday to Friday), or on `days`. */
export interface Schedule {
  every: 'hour' | 'day' | 'weekdays' | 'week';
  /** HH:MM, 24-hour. */
  at: string;
  /** `week` only, and at least one. */
  days?: Weekday[];
}

/** How an automation's last run went: `started`, its chat made and the reply
 *  begun; `skipped`, over the usage limit or with the run before it still
 *  waiting for you, and no chat made; `failed`, it could not start. */
export type AutomationOutcome = 'started' | 'skipped' | 'failed';

/** A prompt Assist runs on a schedule, in the cloud. Each run is a chat of
 *  its own (`ChatSummary.automationId`), and tells the phone as any reply does. */
export interface Automation {
  id: string;
  name: string;
  prompt: string;
  schedule: Schedule;
  /** IANA, such as Europe/London. */
  timeZone: string;
  /** A `ModelInfo.id` that takes tools. */
  model: string;
  /** Its runs may use Research. */
  research: boolean;
  projectId: string | null;
  enabled: boolean;
  /** The schedule in words: "Weekdays at 09:00". */
  when: string;
  /** Unix seconds; null while it is off. */
  nextRunAt: number | null;
  lastRun: { at: number; outcome: AutomationOutcome; chatId: string | null } | null;
  createdAt: number;
  updatedAt: number;
}

export interface AutomationList {
  automations: Automation[];
  /** How many the plan allows; 0 where it has none. */
  max: number;
}

/** POST /automations. `timeZone` defaults to the settings', `model` to the
 *  default model, `enabled` to true. */
export interface AutomationCreate {
  name: string;
  prompt: string;
  schedule: Schedule;
  timeZone?: string;
  model?: string;
  research?: boolean;
  projectId?: string | null;
  enabled?: boolean;
}

export type AutomationPatch = Partial<AutomationCreate>;

/** GET /automations/:id/runs: its chats, newest first. */
export interface AutomationRuns {
  runs: ChatSummary[];
}

/** POST /automations/:id/run (202): the run's chat. */
export interface AutomationRunStarted {
  chatId: string;
}

export const MAX_AUTOMATION_NAME_CHARS = 60;
export const MAX_AUTOMATION_PROMPT_CHARS = 4_000;

/** How many automations each plan allows. Placeholders, as the usage
 *  allowances are (engine/limits.ts). */
export const MAX_AUTOMATIONS: Readonly<Record<string, number>> = { free: 0, pro: 10, max: 25, enterprise: 25, developer: 100 };

// ── dispatch ────────────────────────────────────────────────────────────────

/**
 * The account's PCs: the desktop app on each machine, and one thread for
 * each that a message from the phone or any browser runs in, with what that
 * PC publishes (its local tools, its Mauvely apps' MCP tools, computer use).
 * The contract for the desktop's half is docs/dispatch.md.
 */

/** What a PC runs. */
export type DevicePlatform = 'windows' | 'linux' | 'macos';
export const DEVICE_PLATFORMS: readonly DevicePlatform[] = ['windows', 'linux', 'macos'];

/** The id the desktop app made for itself, kept on the machine and the same
 *  across sign-ins. */
export const DEVICE_ID = /^[A-Za-z0-9_-]{8,64}$/;
/** The longest name a PC goes by. */
export const MAX_DEVICE_NAME_CHARS = 64;
/** The most PCs an account keeps. */
export const MAX_DEVICES = 20;

/** One of the account's PCs. */
export interface Device {
  id: string;
  /** What the desktop app calls the machine. */
  name: string;
  platform: DevicePlatform;
  /** Its desktop app has the account's hub open now. */
  online: boolean;
  /** How many tools it publishes. */
  tools: number;
  /** Its Dispatch thread; null until first opened. */
  chatId: string | null;
  /** When its thread last changed; null until it has one. The PC to open
   *  first is the one with the latest. */
  lastUsedAt: number | null;
  /** When it last published itself, or its last socket closed. */
  lastSeenAt: number;
}

/** GET /devices: the account's PCs, the latest used first, then the ones
 *  never used, latest seen first. */
export interface DeviceList {
  devices: Device[];
}

/** PUT /devices/:id — from the desktop app alone, on start and whenever its
 *  tools change: what it is and what it can run. Answers with the `Device`.
 *  `tools` is held to `TurnRequest.clientTools`' limits. */
export interface DevicePut {
  name: string;
  platform: DevicePlatform;
  tools: ClientTool[];
}

/** POST /devices/:id/report — from that PC's own session alone: what it is
 *  doing with a reply in its thread that waits on it. `running`: it has
 *  taken the reply on, or the person there has answered. `asking`: a sheet
 *  of its own asks the person there about `callId`. 204. */
export interface DeviceReport {
  messageId: string;
  state: 'running' | 'asking';
  /** `asking` only: one of the calls the reply waits on. */
  callId?: string;
  /** `asking` only: the PC's request that a paired phone may answer its
   *  sheet for that call. */
  approval?: ApprovalRequest;
}

/** Why a reply in a PC's thread has not gone on. `offline`: the PC was not
 *  connected when the reply paused; it is woken when it is back. `asking`:
 *  the PC is asking the person there about `callId`. */
export interface DeviceWait {
  state: 'offline' | 'asking';
  /** `asking` only. */
  callId?: string;
  /** Unix seconds. */
  since: number;
  /** `asking` only: what a phone paired with the PC may answer, when the PC
   *  made a request for its sheet. */
  approval?: ApprovalRequest;
  /** The request `approval` replaced, still good until it expires. */
  prior?: { nonce: string; expires: number };
}

/**
 * Approving from a paired phone (docs/dispatch.md, "Approving from a paired
 * phone"). The PC makes a request for one of its sheets; a phone paired with
 * it draws the sheet and signs its answer with a key in its keystore; the PC
 * checks the signature before anything runs. The Worker carries both and can
 * make neither, so nothing here is checked for more than its shape.
 */

/** A phone's key id: the first 16 bytes of SHA-256 of its public key's DER,
 *  lower-case hex. */
export const PHONE_KEY_ID = /^[0-9a-f]{32}$/;
/** A request's nonce: 16 random bytes, base64url. */
export const APPROVAL_NONCE = /^[A-Za-z0-9_-]{22}$/;
/** The longest a request may be good for, from when the Worker hears of it. */
export const MAX_APPROVAL_SECONDS = 900;
/** The most of a sheet the Worker keeps, as JSON. */
export const MAX_APPROVAL_SHEET_BYTES = 65_536;
export const MAX_SHEET_WHAT_CHARS = 200;
export const MAX_SHEET_ROWS = 4;
export const MAX_SHEET_LABEL_CHARS = 40;
export const MAX_SHEET_VALUE_CHARS = 4_096;
export const MAX_SHEET_NOTE_CHARS = 400;
/** How long the Worker keeps a pairing's state. */
export const PAIRING_SECONDS = 600;

/** What one of the PC's sheets shows, in full and in order. */
export interface ApprovalSheet {
  /** Its heading: "Write a file". */
  what: string;
  /** Each path, with its label: `""`, or `From` and `To` for a move. */
  rows: Array<{ label: string; value: string }>;
  /** "Creates a new file.", or `""`. */
  note: string;
  /** The text of a write, or an MCP tool's arguments pretty-printed; `""`. */
  text: string;
}

/** The PC's request that a paired phone may answer one of its sheets. */
export interface ApprovalRequest {
  v: 1;
  /** The PC's `Device.id`. */
  pc: string;
  nonce: string;
  /** The id the call was run under: `<messageId>:<the model's call id>`. */
  callId: string;
  /** Unix seconds. */
  expires: number;
  /** SHA-256 of the sheet's encoding, lower-case hex. */
  digest: string;
  sheet: ApprovalSheet;
}

/** POST /devices/:id/approvals/:nonce — a paired phone's answer, signed. 202
 *  once on its way to the PC. */
export interface ApprovalAnswer {
  messageId: string;
  phone: string;
  decision: 'allow' | 'deny';
  /** ECDSA P-256, DER, base64url. */
  signature: string;
}

/** POST /devices/:id/pair — a phone asking to be paired, with its proof of
 *  the PC's code. 202 `PairingStatus` once on its way to the PC. */
export interface PairRequest {
  v: 1;
  phone: string;
  /** What the phone calls itself. */
  name: string;
  /** Its public key: SubjectPublicKeyInfo, DER, base64url. */
  key: string;
  /** The code's. */
  expires: number;
  /** HMAC-SHA256 under the code's secret, base64url. */
  mac: string;
  /** The new key's signature, base64url. */
  sig: string;
}

/** PUT /devices/:id/pairings/:phone — from the PC's own session: what it
 *  answered. */
export interface PairingAnswer {
  ok: boolean;
}

/** GET /devices/:id/pairings/:phone — where a pairing stands. `unknown`:
 *  none, or none in the last 10 minutes. */
export interface PairingStatus {
  state: 'waiting' | 'paired' | 'refused' | 'unknown';
}

// ── memory ──────────────────────────────────────────────────────────────────

export type MemoryCategory = 'you' | 'topics' | 'work';

export interface MemoryDoc {
  id: string;
  category: MemoryCategory;
  name: string;
  summary: string;
  details: string[];
  updatedAt: number;
}

/** POST /memory/edit — "Tell Assist what to remember", or with `docId`,
 *  "what to change or remove" in that doc. Synchronous: answers once applied. */
export interface MemoryEditRequest {
  instruction: string;
  docId?: string;
}

export interface MemoryEditResult {
  docs: MemoryDoc[];
  changes: MemoryChange[];
}

/** POST /memory/import — another assistant's memory, pasted or from a file,
 *  taken on part by part. Answers with a stream of `MemoryImportEvent`s. */
export interface MemoryImportRequest {
  text: string;
}

export const MAX_MEMORY_IMPORT_CHARS = 100_000;

/** `part` comes once per part, in order from `index` 0, with what that part
 *  changed. An `error` ends the stream; the parts before it stay applied. */
export type MemoryImportEvent =
  | { type: 'start'; parts: number }
  | { type: 'part'; index: number; changes: MemoryChange[] }
  | { type: 'done'; docs: MemoryDoc[] }
  | { type: 'error'; code: ErrorCode; message: string; resetsAt?: number };

// ── usage ───────────────────────────────────────────────────────────────────

export interface UsageByModel {
  model: string;
  tokensIn: number;
  tokensOut: number;
  units: number;
}

export interface UsageReport {
  plan: string;
  summary: UsageSummary;
  /** The last seven days, Mauvely-billed calls only. */
  byModel: UsageByModel[];
}

// ── onboarding ──────────────────────────────────────────────────────────────

/** GET /onboarding — the interview so far, to resume after a reload. */
export interface OnboardingState {
  messages: Message[];
  finished: boolean;
}

/** POST /onboarding/finish. `skip` ends it without reading the answers. */
export interface OnboardingFinish {
  skip: boolean;
}

// ── gallery ─────────────────────────────────────────────────────────────────

/** The shapes an image can be made in, each about a megapixel. */
export const ASPECT_RATIOS = ['1:1', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16'] as const;
export type AspectRatio = (typeof ASPECT_RATIOS)[number];

export type ImageModelKey =
  | 'flux-2-dev'
  | 'flux-2-klein-4b'
  | 'flux-2-klein-9b'
  | 'lucid-origin'
  | 'phoenix-1.0'
  | 'gpt-image-2.5-sunburst'
  | 'nano-banana-pro'
  | 'nano-banana-2';

export interface GalleryModel {
  key: ImageModelKey;
  name: string;
  maker: string;
  /** What it is best at, in a few words. */
  note: string;
  /** How many images it can work from; 0 for none. */
  maxReferences: number;
  /** Whether effort changes anything: false for a model with fixed steps. */
  efforts: boolean;
}

/** The Gallery's models, in menu order. */
export const GALLERY_MODELS: readonly GalleryModel[] = [
  { key: 'flux-2-dev', name: 'FLUX.2 [dev]', maker: 'Black Forest Labs', note: 'Most detailed', maxReferences: 4, efforts: true },
  { key: 'flux-2-klein-9b', name: 'FLUX.2 [klein] 9B', maker: 'Black Forest Labs', note: 'Best at edits', maxReferences: 4, efforts: false },
  { key: 'flux-2-klein-4b', name: 'FLUX.2 [klein] 4B', maker: 'Black Forest Labs', note: 'Fastest', maxReferences: 4, efforts: false },
  { key: 'lucid-origin', name: 'Lucid Origin', maker: 'Leonardo', note: 'Photographic', maxReferences: 0, efforts: true },
  { key: 'phoenix-1.0', name: 'Phoenix 1.0', maker: 'Leonardo', note: 'Text in images', maxReferences: 0, efforts: true },
  { key: 'gpt-image-2.5-sunburst', name: 'GPT Image 2.5', maker: 'OpenAI', note: 'Most capable', maxReferences: 4, efforts: false },
  { key: 'nano-banana-pro', name: 'Nano Banana Pro', maker: 'Google', note: 'Highest quality', maxReferences: 3, efforts: false },
  { key: 'nano-banana-2', name: 'Nano Banana 2', maker: 'Google', note: 'Fast and sharp', maxReferences: 3, efforts: false }
];

export const DEFAULT_GALLERY_MODEL: ImageModelKey = 'flux-2-dev';
/** The most images one Create makes. */
export const MAX_GALLERY_COUNT = 4;
/** The most images one Create works from. */
export const MAX_GALLERY_REFERENCES = 4;

/** An image or a video in the Gallery. An image's bytes are
 *  GET /gallery/:id/image, a video's GET /gallery/:id/video. */
export interface GalleryItem {
  id: string;
  kind: 'image' | 'video';
  /** The Create it came from; its siblings share it. */
  batchId: string;
  model: ImageModelKey | VideoModelKey;
  /** What was asked for. */
  prompt: string;
  /** What the model was given, when "Improve prompt" rewrote it. */
  usedPrompt: string | null;
  aspect: AspectRatio;
  /** Null for a model with fixed steps. */
  effort: Effort | null;
  /** The Gallery images it worked from. */
  references: string[];
  /** The image "Change this…" made it from; null for an original. */
  parentId: string | null;
  /** The original of its thread; null for an original. */
  rootId: string | null;
  /** An original's revisions, in the list; 0 everywhere else. */
  revisions: number;
  width: number;
  height: number;
  /** A video's length; null for an image. */
  seconds: number | null;
  createdAt: number;
}

/** GET /gallery?before= — originals, newest first; `next` is the next page's
 *  `before`. Revisions are in their thread. */
export interface GalleryList {
  items: GalleryItem[];
  next: string | null;
}

/** GET /gallery/:id/thread — the original, then its revisions, oldest first.
 *  Any image of the thread finds it. */
export interface GalleryThread {
  items: GalleryItem[];
}

/**
 * POST /gallery takes a multipart body: this as the `request` part, and up to
 * `MAX_GALLERY_REFERENCES` images to work from as `reference` parts, which
 * are used and never stored. Answers with a stream of `GalleryEvent`s.
 */
export interface GalleryRequest {
  prompt: string;
  model: ImageModelKey;
  aspect: AspectRatio;
  effort?: Effort;
  /** 1 to `MAX_GALLERY_COUNT`. */
  count: number;
  /** Rewrite the prompt the way the models are best asked. */
  improve: boolean;
  /** Gallery images to work from, as well as any uploaded. */
  references?: string[];
  /** "Change this…": the Gallery image to change. It is the one image worked
   *  from, `count` is 1, and what is made is kept as its revision. */
  parent?: string;
}

export type GalleryEvent =
  /** The prompt as rewritten, when "Improve prompt" was on. */
  | { type: 'prompt'; used: string }
  | { type: 'start'; batchId: string; count: number; width: number; height: number }
  | { type: 'image'; index: number; item: GalleryItem }
  | { type: 'failed'; index: number; message: string }
  | { type: 'usage'; usage: UsageSummary }
  | { type: 'error'; code: ErrorCode; message: string; resetsAt?: number }
  | { type: 'done' };

// ── video ───────────────────────────────────────────────────────────────────

/** Video models. Veo through Cloudflare's Unified Billing, at 720p. */
export type VideoModelKey = 'veo-3.1-fast';
export const VIDEO_MODEL_NAME: Record<VideoModelKey, string> = { 'veo-3.1-fast': 'Veo 3.1 Fast' };
export const VIDEO_ASPECTS = ['16:9', '9:16', '1:1'] as const;
export type VideoAspect = (typeof VIDEO_ASPECTS)[number];
export const VIDEO_SECONDS = [4, 6, 8] as const;
export type VideoSeconds = (typeof VIDEO_SECONDS)[number];

/** How many videos each plan may start in a day. Placeholders, as the usage
 *  allowances are (engine/limits.ts). */
export const MAX_VIDEOS_PER_DAY: Readonly<Record<string, number>> = { free: 0, pro: 2, max: 5, enterprise: 5, developer: 10 };

/** POST /gallery/videos — a video made outside any chat. Answers 202 with
 *  the `VideoJob`; the account's hub says when it is done (`HubServerFrame`
 *  `video`). */
export interface VideoRequest {
  prompt: string;
  aspect: VideoAspect;
  seconds: VideoSeconds;
}

/** A video being made, or made. */
export interface VideoJob {
  id: string;
  prompt: string;
  aspect: VideoAspect;
  seconds: VideoSeconds;
  status: 'queued' | 'running' | 'done' | 'failed';
  /** The Gallery item, once done. */
  galleryId: string | null;
  createdAt: number;
}

/** GET /gallery/videos — the Gallery's videos still being made. */
export interface VideoJobList {
  jobs: VideoJob[];
}

// ── dictation ───────────────────────────────────────────────────────────────

/** POST /transcribe takes the recording as the raw body (`audio/webm` or
 *  similar) and answers with the text. */
export interface Transcript {
  text: string;
}

// ── listening and speech ────────────────────────────────────────────────────

/** `dictate` hears words for the composer; `converse` hears turns for voice
 *  mode, and English only. */
export type ListenMode = 'dictate' | 'converse';

/** The audio `/listen` takes: signed 16-bit little-endian mono at this rate. */
export const LISTEN_SAMPLE_RATE = 16_000;

/**
 * GET /listen?mode= is upgraded to a WebSocket. The page sends audio as
 * binary frames of at most half a second, and `ListenControl` as text; the
 * Worker sends `ListenEvent`s as text. A refusal — the limit, AI switched
 * off — is an `error` and then `end`, never a failed handshake.
 */
export type ListenControl = { type: 'finish' };

export type ListenEvent =
  | { type: 'ready'; maxSeconds: number }
  /** Dictation: a phrase, sent again as it firms up until it is `final`. */
  | { type: 'text'; text: string; final: boolean }
  /** Voice mode: a turn starting, growing, ending, or picking up again. */
  | { type: 'turn'; phase: 'start' | 'update' | 'end' | 'resumed'; index: number; text: string }
  | { type: 'error'; code: ErrorCode; message: string; resetsAt?: number }
  | { type: 'end'; reason: 'finished' | 'limit' | 'timeout' | 'failed' };

/** The voices voice mode speaks in (Deepgram Aura-2). */
export const VOICES = ['luna', 'thalia', 'andromeda', 'helena', 'pandora', 'apollo', 'arcas', 'orion', 'draco'] as const;
export type VoiceName = (typeof VOICES)[number];
export const DEFAULT_VOICE: VoiceName = 'luna';

/** The longest piece `/speak` says in one go. */
export const MAX_SPEAK_CHARS = 600;

/** POST /speak — answers with `audio/wav`. `voice` is for trying one out;
 *  otherwise the setting's. */
export interface SpeakRequest {
  text: string;
  voice?: VoiceName;
}

// ── API keys ────────────────────────────────────────────────────────────────

/**
 * A key for Assist's API, on the Developer plan alone (docs/api.md). Every
 * key reaches the OpenAI-compatible routes under `/api/assist/v1`. `chats`
 * also lets it use the chat routes the app uses, and `memory` the account's
 * memory, in replies and at `/memory`.
 */
export interface ApiKey {
  id: string;
  name: string;
  /** The key's last four characters. The key itself is shown once. */
  hint: string;
  chats: boolean;
  memory: boolean;
  createdAt: number;
  /** Null until it is first used. Counted to the minute. */
  lastUsedAt: number | null;
}

/** POST /keys. */
export interface CreateApiKeyRequest {
  name: string;
  chats: boolean;
  memory: boolean;
}

/** What POST /keys answers: the key, whole, this once. */
export interface CreatedApiKey {
  key: ApiKey;
  token: string;
}

/** PATCH /keys/:id. */
export type ApiKeyPatch = Partial<CreateApiKeyRequest>;

/** Every key starts with this. */
export const API_KEY_PREFIX = 'asst_';

export const MAX_API_KEYS = 20;

// ── list envelopes ──────────────────────────────────────────────────────────

export interface ApiKeyList {
  keys: ApiKey[];
}
export interface ChatList {
  chats: ChatSummary[];
}
export interface ProjectList {
  projects: ProjectSummary[];
}
export interface MemoryList {
  docs: MemoryDoc[];
}
export interface ProviderList {
  providers: ProviderStatus[];
}
